import type { Transaction as SuiTransaction } from '@mysten/sui/transactions';
import type {
  FinalExecutionOutcome,
  FunctionCallAction,
  Transaction,
  Wallet,
} from '@near-wallet-selector/core';
import type { WalletContextState as SolanaWallet } from '@solana/wallet-adapter-react';
import type {
  Connection,
  VersionedTransaction as SolanaTransaction,
} from '@solana/web3.js';
import type { WalletContextState } from '@suiet/wallet-kit';
import type {
  Message as AIMessage,
  ChatRequestOptions,
  CreateMessage,
  UIMessage,
} from 'ai';
import type BN from 'bn.js';
import type { Account } from 'near-api-js';
import type { OpenAPIV3 } from 'openapi-types';
import type { ComponentType } from 'react';
import type { Transaction as EVMTransaction, Hash, Hex, Signature } from 'viem';
import type { Config } from 'wagmi';
import type {
  SendTransactionMutate,
  SignMessageMutate,
  SignTypedDataMutate,
  SwitchChainMutate,
} from 'wagmi/query';
import type { EthSendTransactionRequest, SignRequest } from './evm';

export * from './evm';

export const ChainType = {
  EVM: 'evm',
  NEAR: 'near',
  SUI: 'sui',
} as const;

export type ChainType = (typeof ChainType)[keyof typeof ChainType];

// Core Types
export type BitteMetadata = Record<string, unknown>;

export type JSONValue =
  | string
  | number
  | boolean
  | null
  | { [key: string]: JSONValue }
  | JSONValue[];

// Tool Types
export type FunctionDefinition = {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
};

export type PluginToolSpec = {
  id: string;
  agentId: string;
  type: 'function';
  function: FunctionDefinition;
  execution: ExecutionDefinition;
  verified: boolean;
};

export type ExecutionDefinition = {
  baseUrl: string;
  path: string;
  httpMethod: string;
};

export type FunctionTool = {
  type: 'function';
  function: FunctionDefinition;
};

export type CoreTool = unknown;
export type AssistantTool = unknown;
export type BitteToolSpec = PluginToolSpec | FunctionTool;

// Rename to BitteCoreTool to avoid confusion with primitives.BitteTool
export type BitteCoreTool<
  TArgs = Record<string, JSONValue>,
  TResult = unknown,
> = {
  toolSpec: FunctionTool;
  execute?: BitteToolExecutor<TArgs, TResult>;
  chainIds?: string[];
};

export type BitteToolExecutor<
  TArgs = Record<string, JSONValue>,
  TResult = unknown,
> = (
  args: TArgs,
  metadata?: BitteMetadata,
) => Promise<BitteToolResult<TResult>>;

// For backward compatibility

export type BitteToolResult<TResult = unknown> =
  | { data: TResult; error?: never; prompt?: string }
  | { data?: never; error: string };

export type BitteToolWarning = {
  message: string;
  final: boolean;
};

// Agent Types
export type BittePrimitiveRef = {
  type: string;
};

export type BitteAgentConfig = {
  id: string;
  name: string;
  accountId?: string | null;
  description: string;
  instructions: string;
  verified: boolean;
  tools?: BitteToolSpec[];
  image?: string | null;
  repo?: string | null;
  categories?: string[];
  chainIds?: number[];
  primitives?: string[];
};

export type BitteAgent = Omit<BitteAgentConfig, 'tools'> & {
  toolSpecs?: FunctionTool[];
  tools?: Record<string, CoreTool>;
};

export type BitteAgentBase = {
  name: string;
  description: string;
  instructions: string;
  tools?: BitteToolSpec[];
  image?: string;
  chainIds?: number[];
  categories?: string[];
  repo?: string;
};

export type AgentMetadata = {
  evmAddress?: string;
  accountId?: string;
  network: string;
  localAgent?: LocalAgentSpec;
  agentId: string;
};

export type LocalAgentSpec = {
  pluginId: string;
  accountId: string;
  spec: BitteOpenAPISpec;
};

export type BitteOpenAPISpec = OpenAPIV3.Document & {
  'x-mb': {
    'account-id'?: string;
    assistant?: Pick<
      BitteAssistantConfig,
      'name' | 'description' | 'instructions' | 'tools' | 'image'
    > & {
      tools?: (AssistantTool | BittePrimitiveRef)[];
    };
  };
};

export type BitteAssistantConfig = {
  id: string;
  name: string;
  accountId: string;
  description: string;
  instructions: string;
  verified: boolean;
  tools?: BitteToolSpec[];
  image?: string;
};

export type BitteAssistant = Omit<BitteAssistantConfig, 'tools'> & {
  toolSpecs?: FunctionTool[];
  tools?: Record<string, CoreTool>;
};

// BitteAI configuration
export interface BitteAIOptions {
  apiKey?: string;
  baseUrl?: string;
  timeout?: number;
}

// These types are imported from external packages but defined here temporarily
// We're using Record<string, unknown> instead of any to satisfy the linter
export interface NearTransaction {
  signerId: string;
  receiverId: string;
  actions: unknown[];
}

export interface EVMWalletAdapter {
  sendTransaction: SendTransactionMutate<Config, unknown>;
  // Signing methods for different types of sign requests
  signMessage?: SignMessageMutate<unknown>;
  signTypedData?: SignTypedDataMutate<unknown>;
  switchChain: SwitchChainMutate<Config, unknown>;
  address: string | `0x${string}` | undefined;
  chainId?: number;
  hash?: Hash | string | `0x${string}` | undefined;
  signature?: Signature;
  authorization?: string | `0x${string}`;
}

export interface CardanoSignRequest {
  transactionBytes: string;
  partialSign?: boolean;
}

export interface CardanoWalletAdapter {
  signTx?: (unsignedTx: string, partialSign?: boolean) => Promise<string>;
  submitTx?: (signedTx: string) => Promise<string>;
  address?: string;
}

// This enum is used both as a type and a runtime value in the chat package
export type AssistantsMode = 'default' | 'debug';

export interface ToolInvocation {
  toolName: string;
  toolCallId: string;
  state: 'result' | 'running' | 'error';
  args: Record<string, JSONValue> & {
    message?: string;
    nonce?: string;
    recipient?: string;
    callbackUrl?: string;
  };
  result?: {
    data?:
      | {
          transactions?: unknown[];
          warnings?: string[];
          suiTransactionBytes?: unknown;
          evmSignRequest?: unknown;
          url?: string;
          // Chart properties
          title?: string;
          description?: string;
          chartConfig?: unknown;
          chartData?: unknown;
          metricData?: unknown;
          metricLabels?: unknown;
          dataFormat?: string;
          chartType?: string;
        }
      | JSONValue;
    error?: string;
  };
}

export interface MessagePart {
  type: 'tool-invocation' | string;
  toolInvocation?: ToolInvocation;
}

export type SmartAction = {
  id: string;
  agentId: string;
  message: string;
  creator: string;
  createdAt: number;
};

export type SmartActionMessage = {
  id?: string;
  agentId?: string;
  content: string;
  role: ExtendedMessageRole;
  parts?: UIMessage['parts'];
};

// Extend the Message type to include data role from ai package
export type ExtendedMessageRole =
  | 'user'
  | 'assistant'
  | 'system'
  | 'function'
  | 'data'
  | 'tool';

export type SmartActionAiMessage = Omit<Message, 'role'> & {
  id?: string;
  agentId?: string;
  agentImage?: string;
  agentName?: string;
  parts?: MessagePart[];
  role: ExtendedMessageRole;
};

export type SmartActionChat = SmartAction & {
  messages: SmartActionMessage[];
};

export type SaveSmartAction = {
  agentId: string;
  creator: string;
  message: string;
};

export type SaveSmartActionMessages = {
  id: string;
  agentId: string;
  creator: string;
  messages: SmartActionMessage[];
};

// Base token information
export interface BaseTokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  icon?: string; // generate-evm-transaction.ts (CowSwap)
  tokenIcon?: string; // get-portfolio.ts
  contractAddress?: string;
}

// Token metadata with additional properties
export interface TokenMeta extends BaseTokenInfo {
  isSpam?: boolean;
}

// Token info with balance/value information
export interface TokenInfo extends BaseTokenInfo {
  amount: string;
  usdValue: number;
}
// Base definition for swap data
export interface SwapFTData {
  network: {
    name: string;
    icon: string;
  };
  type: 'swap';
  fee: string;
  tokenIn: TokenInfo;
  tokenOut: TokenInfo;
}

// Transfer data using the TokenInfo interface
export interface TransferFTData {
  network: {
    name: string;
    icon: string;
  };
  type: 'transfer-ft';
  sender: string;
  receiver: string;
  token: TokenInfo; // Reuse TokenInfo instead of duplicating fields
}

export interface TransferResponse {
  data: TransferFTData;
  evmSignRequest: EthSendTransactionRequest;
  type: 'transfer-ft';
}

export interface SwapResponse {
  data: SwapFTData;
  evmSignRequest: EthSendTransactionRequest;
  type: 'swap';
}

// Primitive: get-portfolio.ts
export interface PriceChange {
  day: number;
  week: number;
  month: number;
}

export type EVMTransactionAction = FunctionCallAction | TransferAction;

export interface NearTransferAction {
  type: 'Transfer';
  params: {
    deposit: string;
  };
}

export type AccountEVMTransaction = Omit<EVMTransaction, 'actions'> & {
  actions: Array<FunctionCallAction | TransferAction>;
};

export interface PriceData {
  day: [number, number][];
  week: [number, number][];
  month: [number, number][];
}

export interface TokenBalances {
  balance: number;
  usdBalance: number;
  price?: number;
}

export interface BaseAsset {
  symbol: string;
  usdValue: number;
  chains: string[];
  icon: string | null;
  balances: TokenBalances;
  meta: TokenMeta;
}

export interface EnhancedAsset extends BaseAsset {
  priceChanges?: PriceChange;
  priceData?: PriceData;
  volume24h: number;
  marketcap: number;
}

export interface WalletAgentContext {
  portfolioValue: number;
  chainsWithGas: string[];
  significantAssets: BaseAsset[];
  actionableSummary: string;
}

export interface PortfolioResponse extends WalletAgentContext {
  significantAssets: EnhancedAsset[];
}

export interface TransferFtResult {
  EVMTransactions: AccountEVMTransaction[];
  warnings?: string[];
}

export interface GenerateEVMTransactionParams {
  EVMTransactions: AccountEVMTransaction[];
  network?: 'mainnet' | 'testnet';
}

export type TransferFtParams = {
  signerId: string;
  receiverId: string;
  tokenIdentifier: string;
  amount: string;
  network?: 'mainnet' | 'testnet';
};

// Define specific props types for each tool
export type PortfolioComponentProps = {
  data: PortfolioResponse;
  sendPrompt?: SendPromptFunction;
};

export type TransferToolProps = {
  data: TransferFTData;
  sendPrompt?: SendPromptFunction;
};

export type SwapComponentDataProps = {
  data: SwapFTData;
  sendPrompt?: SendPromptFunction;
};

// Base tool interfaces - updated for better type consistency
export interface BaseTool<T = unknown> {
  name: string;
  component: React.ComponentType<T>;
}

// BitteTool wrapper that's consistent across the codebase
export interface BitteTool<T = unknown> extends BaseTool<T> {
  // Additional properties specific to Bitte tools can be added here
}

export interface TransferTool extends BitteTool<TransferToolProps> {
  name: 'transfer-ft';
}

export interface PortfolioTool extends BitteTool<PortfolioComponentProps> {
  name: 'portfolio';
}

export interface SwapTool extends BitteTool<SwapComponentDataProps> {
  name: 'swap';
}

// Primitive: generate-evm-EVMTransaction.ts
export interface GenerateImageResponse {
  url: string;
  hash: string;
}

// Primitive: sign-message.ts
export interface SignMessageParams {
  message: string;
  callbackUrl: string;
  recipient: string;
  nonce: string;
}

export interface SignMessageResult {
  accountId: string;
  publicKey: string;
  signature: string;
  message: string;
  nonce: string;
  recipient: string;
  callbackUrl: string;
  state: string;
}

// Convert enum to string literal type
export type BittePrimitiveName =
  | 'transfer-ft'
  | 'generate-EVMTransaction'
  | 'submit-query'
  | 'generate-image'
  | 'getSwapEVMTransactions'
  | 'getTokenMetadata'
  | 'generate-evm-tx'
  | 'generate-sui-tx'
  | 'generate-sol-tx'
  | 'render-chart'
  | 'sign-message'
  | 'get-portfolio'
  | 'aftermath-sui-swap'
  | 'get-sui-balances'
  | 'sui-lst';

// ----------------- Transaction Action Types -----------------
export interface TransferAction {
  type: 'Transfer';
  params: Record<string, unknown>;
}

export interface NearFunctionCallAction {
  type: 'FunctionCall';
  params: {
    methodName: string;
    args: Record<string, JSONValue>;
    deposit?: string;
    gas?: string;
  };
}
// ----------------- Widget Chat Props -----------------
export interface WidgetChatProps {
  widgetWelcomePrompts?: WelcomeQuestionAction;
  customTriggerButton?: React.ReactElement;
  triggerButtonStyles?: {
    backgroundColor?: string;
    logoColor?: string;
  };
}

// ----------------- Message Type -----------------
export interface Message {
  id: string;
  role: ExtendedMessageRole;
  content: string;
}

// ----------------- Chat Component Types -----------------
export type ChatComponentColors = {
  generalBackground?: string;
  messageBackground?: string;
  textColor?: string;
  buttonColor?: string;
  borderColor?: string;
};

export type SendPromptFunction = (prompt: string) => Promise<void> | void;

/* Custom Components */
export interface MessageGroupComponentProps {
  message: SmartActionAiMessage;
  isUser: boolean;
  userName: string;
  children: React.ReactNode;
  style: {
    backgroundColor: string;
    borderColor: string;
    textColor: string;
  };
  uniqueKey: string;
  showBorder?: boolean;
}

export interface ChatContainerComponentProps {
  children: React.ReactNode;
  style?: {
    backgroundColor?: string;
    borderColor?: string;
  };
}

export interface InputContainerProps {
  children: React.ReactNode;
  style?: {
    backgroundColor?: string;
    borderColor?: string;
  };
}

export interface LoadingIndicatorComponentProps {
  textColor?: string;
}

export interface SendButtonComponentProps {
  input: string;
  isLoading: boolean;
  buttonColor?: string;
  textColor?: string;
  onClick?: () => void;
}

// ----------------- Transaction Component Types -----------------
export interface TransactionButtonProps {
  onClick: ButtonHandler;
  disabled?: boolean;
  isLoading?: boolean;
  label?: string;
}

// Container props
export interface TransactionContainerProps {
  children: React.ReactNode;
  style: {
    backgroundColor: string;
    borderColor: string;
    textColor: string;
  };
}

// Custom components interface
export interface CustomTransactionComponents {
  Container?: React.ComponentType<TransactionContainerProps>;
  ApproveButton?: React.ComponentType<TransactionButtonProps>;
  DeclineButton?: React.ComponentType<TransactionButtonProps>;
}

// ----------------- Chat Configuration -----------------
export interface WelcomeQuestionAction {
  title?: string;
  description?: string;
  questions?: string[];
  actions?: string[];
}

// Add this new type for welcome configuration
export type WelcomeComponentConfig =
  | { type: 'component'; component: JSX.Element }
  | {
      type: 'account-overview';
      info: Partial<WelcomeQuestionAction>;
      data: PortfolioResponse;
    };

export interface BitteAiChatOptions {
  agentName?: string;
  agentImage?: string;
  chatId?: string;
  prompt?: string;
  localAgent?: {
    pluginId: string;
    accountId: string;
    spec: BitteOpenAPISpec;
  };
  mcpServerUrl?: string;
  placeholderText?: string;
  colors?: ChatComponentColors;
  customComponents?: {
    welcomeMessageComponent?: WelcomeComponentConfig;
    mobileInputExtraButton?: JSX.Element;
    messageContainer?: ComponentType<MessageGroupComponentProps>;
    chatContainer?: ComponentType<ChatContainerComponentProps>;
    inputContainer?: ComponentType<InputContainerProps>;
    sendButtonComponent?: ComponentType<SendButtonComponentProps>;
    loadingIndicator?: ComponentType<LoadingIndicatorComponentProps>;
  };
  hideToolCall?: boolean;
}

// Common props for custom tool components - support exact types from primitives
export interface CustomToolComponentProps {
  data:
    | PortfolioResponse
    | TransferResponse
    | SwapResponse
    | Record<string, JSONValue>;
  type: ToolName;
}

// Define custom tool component
export interface ReactToolComponent {
  name: string;
  component: React.ComponentType<CustomToolComponentProps>;
}

// Update CustomToolComponent type to align with BitteTool from primitives.ts
export type CustomToolComponent =
  | PortfolioTool
  | TransferTool
  | SwapTool
  | ReactToolComponent;

export type ToolName = 'get-portfolio' | 'transfer-ft' | 'swap';

export interface ToolConfigMap {
  'get-portfolio': {
    component: React.ComponentType<PortfolioComponentProps>;
    getToolData: (data: unknown) => PortfolioResponse;
  };
  'transfer-ft': {
    component: React.ComponentType<TransferToolProps>;
    getToolData: (data: unknown) => TransferFTData;
  };
  swap: {
    component: React.ComponentType<SwapComponentDataProps>;
    getToolData: (data: unknown) => SwapFTData;
  };
}

// ----------------- Account Types -----------------
export interface AccountContextType {
  wallet?: Wallet;
  account?: Account;
  accountId: string | null;
  nearWalletId: string | undefined;
  evmWallet?: EVMWalletAdapter;
  evmAddress?: string;
  chainId?: number;
  suiWallet?: WalletContextState;
  suiAddress?: string;
  cardanoWallet?: CardanoWalletAdapter;
  cardanoAddress?: string;
  solanaWallet?: SolanaWallet;
  solanaConnection?: Connection;
}

export type WalletOptions = {
  near?: {
    wallet?: Wallet; // From near-wallet-selector
    account?: Account; // From near-api-js
    accountId: string;
    nearWalletId: string | undefined;
  };
  evm?: EVMWalletAdapter; // Interface matching wagmi hook outputs
  sui?: {
    wallet?: WalletContextState; // From @suiet/wallet-kit
  };
  cardano?: CardanoWalletAdapter; // From @meshsdk/react
  solana?: {
    wallet?: SolanaWallet; // From @solana/wallet-adapter-react
    connection?: Connection; // From @solana/web3.js
  };
};

export interface BitteAiChatProps {
  options?: BitteAiChatOptions;
  agentId: string;
  wallet: WalletOptions;
  apiUrl: string;
  historyApiUrl?: string;
  apiKey?: string;
  messages?: Message[];
  customToolComponents?: CustomToolComponent[];
  onChatLoaded?: () => void;
  onMessageReceived?: (message: Message) => void;
  widget?: WidgetChatProps;
  isWidgetChat?: boolean;
  format?: 'markdown' | 'plaintext';
}

export interface UnifiedChatContentProps extends BitteAiChatProps {
  urlChatId?: string;
  isWidgetChat?: boolean;
}

export interface ChatRequestBody {
  id?: string;
  config?: {
    mode?: string;
    agentId?: string;
    model?: string;
    mcpServerUrl?: string;
    format?: 'markdown' | 'plaintext';
  };
  accountId?: string;
  network?: string;
  evmAddress?: Hex;
  suiAddress?: string;
  nearWalletId?: string;
  cardanoAddress?: string;
  solanaAddress?: string;
  chainId?: number;
  localAgent?: {
    pluginId: string;
    accountId: string;
    spec: BitteOpenAPISpec;
  };
}

export type BitteAiChat = (props: BitteAiChatProps) => JSX.Element;

export interface BaseInfo {
  name: string;
  icon: string;
}

// Rename to avoid collision with the other TokenInfo interface
export interface BaseTokenData extends BaseInfo {
  amount: string;
  usdValue: number;
}

export type TokenTransactionData<T extends 'swap' | 'transfer-ft'> = {
  network: BaseInfo;
  txnData: NearTransaction | SignRequest | SuiTransaction;
} & (T extends 'swap'
  ? {
      type: 'swap';
      tokenIn: BaseTokenData;
      tokenOut: BaseTokenData;
    }
  : {
      type: 'transfer-ft';
      sender: string;
      receiver: string;
      token: BaseTokenData;
    });

export type SwapFTDataResponse = TokenTransactionData<'swap'>;
export type TransferFTDataResponse = TokenTransactionData<'transfer-ft'>;

// Common styling interface
export interface CommonStyleProps {
  textColor: string;
  messageBackgroundColor: string;
  borderColor: string;
}

// Transaction components interface
export interface TransactionComponentProps {
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
}

export interface ReviewSignMessageProps
  extends CommonStyleProps,
    TransactionComponentProps {
  message: string;
  recipient?: string;
  nonce?: string;
  callbackUrl?: string;
  chatId: string | undefined;
  toolCallId: string;
  addToolResult: (result: BitteToolResult<SignMessageResult>) => void;
}

// Use generic type for the result
export interface TransactionResultProps<
  T = {
    evm?: { txHash?: string; chainId: number };
    near?: { receipts?: { transaction: { hash: string } }[] };
    sui?: { digest?: string };
    solana?: { signature?: string };
  },
> {
  result: T;
  accountId: string;
  textColor: string;
}

// ----------------- Input Component Types -----------------
export interface SmartActionsInputProps {
  input: string;
  isLoading: boolean;
  agentName?: string;
  agentImage?: string;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  buttonColor: string;
  borderColor: string;
  textColor: string;
  backgroundColor: string;
  mobileInputExtraButton?: React.JSX.Element;
  customSendButtonComponent?: React.ComponentType<SendButtonComponentProps>;
  placeholderText?: string;
}

export type AIMessageLike = AIMessage | CreateMessage;
export type MessageAppend = (
  message: AIMessageLike,
  chatRequestOptions?: ChatRequestOptions,
) => Promise<string | null | undefined>;

// ----------------- Message Display Types -----------------
export interface MessageGroupProps
  extends CommonStyleProps,
    TransactionComponentProps {
  chatId: string | undefined;
  groupKey: string;
  messages: Message[];
  accountId: string;
  creator?: string;
  isLoading?: boolean;
  agentImage?: string;
  agentName?: string;
  evmAdapter?: EVMWalletAdapter;
  account?: Account;
  wallet?: Wallet;
  addToolResult: (params: {
    toolCallId: string;
    result: BitteToolResult<unknown>;
  }) => void;
  append: MessageAppend;
  customMessageContainer?: React.ComponentType<MessageGroupComponentProps>;
  customToolComponents?: CustomToolComponent[];
  showBorder?: boolean;
  hideToolCall?: boolean;
}

export type TransactionListComponentProps = {
  accountId: string;
  operation?: TransactionOperation;
  transaction: NearTransaction[];
  modifiedUrl: string;
  showDetails: boolean;
  showTxnDetail: boolean;
  setShowTxnDetail: (showTxnDetail: boolean) => void;
  costs: Cost[];
  gasPrice: string;
  borderColor: string;
};

// Generic component props for actions
export interface ActionComponentProps<T> {
  data: T;
  onApprove?: ButtonHandler;
  onExplain?: ButtonHandler;
  onFunctionCall?: ButtonHandler;
  sendPrompt?: SendPromptFunction;
}

export interface SwapComponentProps extends ActionComponentProps<SwapFTData> {}

export interface TransferComponentProps
  extends ActionComponentProps<TransferFTData> {}

export interface NetworkInfo {
  name: string;
  icon: string | React.ReactNode;
}

export interface NetworkBadgeProps {
  network: NetworkInfo;
  iconOverride?: string;
}

export interface WelcomeListProps {
  items: string[];
  label: string;
  bgColor: string;
  onItemClick: (item: string) => void;
}

// Generic type for button action handlers
export type ButtonHandler = () => void;

// Common button options
export interface ButtonOptions {
  label?: string;
  show?: boolean;
  handler?: ButtonHandler;
}

// Common transaction data props
export interface TransactionDataProps {
  transactions?: NearTransaction[];
  evmData?: SignRequest;
  suiTransaction?: SuiTransaction;
  suiTransactionBytes?: string;
  messageId?: string;
  append?: MessageAppend;
  // biome-ignore lint/suspicious/noExplicitAny: External API type
  txnData?: any; // Data to show in the code block
}

export interface ActionHandlers {
  onFunctionCall?: ButtonHandler;
  onExplain?: ButtonHandler;
  onApprove?: ButtonHandler;
}

export interface ActionVisibility {
  showFunctionCall?: boolean;
  showExplain?: boolean;
  showApprove?: boolean;
}

export interface ActionLabels {
  functionCall?: string;
  explain?: string;
  approve?: string;
}

export interface ActionButtonsProps extends TransactionDataProps {
  handlers: ActionHandlers;
  actions?: ActionVisibility;
  labels?: ActionLabels;
}

interface TransactionReceipt {
  transaction: {
    hash: string;
  };
  [key: string]: unknown;
}

export interface TransactionResult {
  near?: {
    receipts: TransactionReceipt[];
    transactions: NearTransaction[];
    encodedTxn?: string;
  };
  sui?: {
    digest: string;
    effects: string;
  };
  evm?: {
    txHash: string;
    chainId: number;
  };
}

export type MessageLike =
  | Message
  | Omit<Message, 'id'>
  | {
      id?: string;
      role: string;
      content: string;
      createdAt: Date | string;
      [key: string]: unknown;
    };

// Message append function type that accepts more generic message types
export type MessageAppendFunction = (
  message: MessageLike,
) => Promise<string | null | undefined>;

export type TransactionApprovalOptions = {
  messageId?: string;
  append?: MessageAppend;
};

export type TransactionStatus = 'success' | 'fail' | 'pending';

export interface TransactionState {
  isLoading: boolean;
  errorMsg: string;
  result: TransactionResult | null;
  txHash?: string;
  txDigest?: string;
}

// Define action types
export type TransactionStateAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_RESULT'; payload: TransactionResult }
  | { type: 'SET_TX_HASH'; payload: string }
  | { type: 'SET_TX_DIGEST'; payload: string }
  | { type: 'RESET_ERROR' };

export type ToolInvocationManagerProps = {
  message: SmartActionAiMessage;
  chatId: string;
  creator?: string;
  isLoading?: boolean;
  borderColor?: string;
  messageBackgroundColor?: string;
  textColor?: string;
  addToolResult: (params: {
    toolCallId: string;
    result: BitteToolResult<unknown>;
  }) => void;
  append: MessageAppend;
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
  customToolComponents?: CustomToolComponent[];
  uniqueKey: string;
};

export type MessageContentProps = {
  message: SmartActionAiMessage;
  borderColor?: string;
  append: MessageAppend;
  customToolComponents?: (CustomToolComponent | ReactToolComponent)[];
};

export type ToolInvocationRendererProps = {
  toolName: string;
  toolCallId: string;
  state: string;
  result: BitteToolResult<PortfolioResponse | TransferFTData | SwapFTData>;
  borderColor?: string;
  customToolComponents?: (CustomToolComponent | ReactToolComponent)[];
  append: MessageAppend;
};

export interface AccountCreationData {
  devicePublicKey: string;
  accountId: string;
  isCreated: boolean;
  txnHash?: string;
}

export interface SuccessInfo {
  near: {
    receipts: FinalExecutionOutcome[];
    transactions: Transaction[];
    encodedTxn?: string;
  };
  sui?: {
    digest: string;
    effects: string;
  };
  cardano?: {
    txHash: string;
  };
  solana?: {
    signature: string;
  };
}

export interface UseTransactionProps {
  account?: Account;
  wallet?: Wallet;
  evmWallet?: EVMWalletAdapter;
  suiWallet?: WalletContextState;
  cardanoWallet?: CardanoWalletAdapter;
  solanaWallet?: SolanaWallet;
  solanaConnection?: Connection;
}

export interface HandleTxnOptions {
  transactions?: Transaction[];
  evmData?: SignRequest;
  suiTransaction?: SuiTransaction;
  cardanoData?: CardanoSignRequest;
  solanaTransaction?: SolanaTransaction;
}

type ActionObject = {
  send: () => Promise<void>;
  show: () => void;
  try: () => void;
};

export type Arguments = {
  autotransfer?: boolean;
  account_id?: string;
  msg?: string;
  token_id?: string;
  metadata?: Metadata | string;
  num_to_mint?: number;
  owner_id?: string;
  royalty_args?: string | number | null;
  split_owners?: string | number | null;
  token_ids_to_mint?: string | number | null;
  receiver_id?: string;
  token_ids?: string[];
  methodName?: string;
  amount?: string | number;
  memo?: string | null;
  nft_contract_id?: string | null;
  title?: string;
  tokenImg?: string;
};

type Metadata = {
  media: string;
  reference: string;
  title?: string;
  description?: string;
};

export type TransactionListProps = {
  transaction: Transaction[];
  operation?: TransactionOperation;
  actions?: ActionObject;
  modifiedUrl: string;
  showDetails: boolean;
  showTxnDetail: boolean;
  setShowTxnDetail: (showTxnDetail: boolean) => void;
};

export type TxnDetailWrapperProps = {
  accountId: string;
  transaction: Transaction[];
  modifiedUrl: string;
  showDetails: boolean;
  showTxnDetail: boolean;
  costs: Cost[];
  gasPrice: string;
};

export type TransactionDetailProps = {
  transaction: Transaction;
  showDetails: boolean;
  modifiedUrl: string;
  gas: string | number;
  deposit: string | number;
};

export type FlattenTransactionResults = {
  hash: string;
  sender: string;
  receiver: string;
  txn: Transaction[];
  type: string;
};

export type ActionCosts =
  | 'CreateAccount'
  | 'Transfer'
  | 'Stake'
  | 'AddFullAccessKey'
  | 'DeleteKey';

export interface NearTransactionState {
  sponsor?: string;
  isLoading: boolean;
  exportLoading: boolean;
  migrationError: string | null;
  migrationLoading: boolean;
  results: FinalExecutionOutcome[] | null;
  error?: { message: string };
}

export type SendTransactionParams = {
  data?: TransactionDetails;
  operation?: TransactionOperation;
  account?: Account;
  disableLoading?: boolean;
  disableSuccess?: boolean;
};

export type NearTransactionParams = {
  transactions: Transaction[];
  account: Account;
};

export type SendTransactionFunction = (
  args?: SendTransactionParams,
) => Promise<SuccessInfo | undefined>;

//FIX ME: IMPROVE TYPES
/* type SponsoringInfo = {
  sponsorId: string;
  paymasterId: string;
  cost: string;
  fee: string;
};
export type TransactionOperationResult =
  | { error: string }
  | { operation: "near" | "credits" | "relay" }
  | ({ operation: "sponsor" } & SponsoringInfo); */

export type TransactionOperation = {
  operation: Operation;
  sponsorId?: string;
  paymasterId?: string;
  cost?: string;
  fee?: string;
};

export interface UrlData {
  successUrl?: string;
  callbackUrl?: string;
}

export interface TransactionDetails {
  transactions: Transaction[];
  isDrop?: boolean;
  evmData?: SignRequest;
}

export interface TransactionProps {
  operation?: TransactionOperation;
  accountData?: AccountCreationData;
  transactionData?: TransactionDetails;
  urlData?: UrlData;
  agentId?: string;
}

export interface TransactionPageProps extends TransactionProps {
  referer: string | null;
}

export interface SendTransactionData {
  operation: TransactionOperation;
  account: Account;
  details: TransactionDetails;
  urlData?: UrlData;
}

export interface TransactionErrorProps {
  error: string;
  transactions: Transaction[];
  accountId: string;
  urlData?: UrlData;
}

export enum Operation {
  NEAR = 'near',
  RELAY = 'relay',
  SPONSOR = 'sponsor',
}

export interface Cost {
  deposit: BN;
  gas: BN;
}

// Define NearReceipt type for transaction results
export interface NearReceipt {
  transaction: {
    hash: string;
  };
  [key: string]: unknown;
}
export interface GenerateSuiTxParams {
  transactionBytes?: string;
  recipientAddress?: string;
  senderAddress?: string;
  amountInSui?: number;
  network?: 'mainnet' | 'testnet' | 'devnet';
}

export interface GenerateSuiTxResult {
  suiTransactionBytes: string;
}

// Primitive: get-sui-balances.ts
export interface SuiBalancesParams {
  address: string;
}

export interface SuiObject {
  objectId: string;
  version: string;
  digest: string;
  type: string;
  owner: {
    AddressOwner: string;
  };
  content: {
    dataType: string;
    type: string;
    hasPublicTransfer: boolean;
    fields: {
      balance: string;
      id: {
        id: string;
      };
    };
  };
}

export interface SuiCoin {
  coinType: string;
  coinObjectCount: number;
  totalBalance: string;
  lockedBalance: {
    epochId: number;
    number: string;
  };
}

export interface SuiBalance {
  coinType: string;
  balance: string;
  name: string;
  symbol: string;
  iconUrl: string;
  decimals: number;
  usdValue: number;
}

export interface SuiBalancesResult {
  balances: SuiBalance[];
  totalUsdValue: number;
}

// Primitive: sui-lst.ts
export interface SuiLstParams {
  address: string;
  amount: string;
  validator: string;
  operation: 'stake' | 'unstake';
}

export interface SuiLstResult {
  suiTransactionBytes: string;
}

// Primitive: aftermath-sui-swap.ts
export interface AftermathSwapParams {
  fromCoin: string;
  toCoin: string;
  amount: number;
  slippage?: number;
  address: string;
}

export interface AftermathSwapResult {
  suiTransactionBytes: string;
}
